// Blueprint: forge/blueprint/frontend/architecture/001-shell-composition.md
import { RefObject, useLayoutEffect } from 'react';

import { resolveScrollport } from './resolve-scrollport';

/** Distance from the top of the viewport to the top edge of the box that scrolls the shell. */
export const SCROLLPORT_TOP = '--org-hub-scrollport-top';

/**
 * Visible height of the box that scrolls the shell. Absent when the document scrolls —
 * the viewport's height is `100dvh`, which only CSS can express, so the stylesheet
 * falls back to it.
 */
export const SCROLLPORT_HEIGHT = '--org-hub-scrollport-height';

/**
 * Publishes where the box that scrolls the shell starts and how tall it is, so
 * `org-hub-page.module.scss` can size and pin Anvil2's `Page` against that box instead
 * of against the viewport.
 *
 * Anvil2's `Page` sizes itself and pins its sticky regions with `100dvh` and `--offset`,
 * which presumes the document is the scroller. The standalone satisfies that; embedded in
 * the monolith the Page lives inside a host pane that starts part-way down the viewport
 * and is itself the scroll container, so both numbers are wrong there: the Page overflows
 * the pane by the height of everything above it, and sticky content pins against the
 * host's header height a second time. Measuring the pane once, here, is what lets one
 * stylesheet serve both hosts — and any future host — without either knowing about the
 * other.
 *
 * `--offset` itself is left as CSS to re-resolve rather than read into a number here, so
 * a host that changes its chrome height after first paint — which the monolith does,
 * measuring its header in a passive effect — needs no observer for that.
 *
 * Re-measured on resize as well as through a `ResizeObserver` on the pane: a dismissed
 * host banner grows the pane without resizing the window. The scrollport's identity is
 * also not fixed — Anvil2 promotes some panes to scroll containers only above a
 * container-query width, and writes `document.body.style.overflow` while the mobile
 * sidebar popover is open — so every pass re-resolves it; a resize is the only thing that
 * changes either, and re-resolving is a short walk.
 *
 * Runs in the layout phase so the properties are committed before the first paint, rather
 * than letting the Page render one frame against the viewport.
 *
 * @param targetRef element to publish onto — must be an ancestor of the `Page`.
 */
export const useScrollportMetrics = (targetRef: RefObject<HTMLElement>): void => {
    useLayoutEffect(() => {
        const target = targetRef.current;
        if (!target) {
            return undefined;
        }

        let observed: Element | null = null;
        let observer: ResizeObserver | undefined;

        /*
         * Deduped and rounded: the properties are inherited by the whole Page subtree, so
         * a write invalidates style for all of it, and a `ResizeObserver` fires on width
         * changes too — an unrounded top would differ by a subpixel on every frame of a
         * window drag.
         */
        const publishedValues = new Map<string, string>();
        const write = (property: string, value: string) => {
            if (publishedValues.get(property) === value) {
                return;
            }
            publishedValues.set(property, value);
            if (value) {
                target.style.setProperty(property, value);
            } else {
                target.style.removeProperty(property);
            }
        };

        const publish = () => {
            const scrollport = resolveScrollport(target);

            /*
             * Observed before it is measured, so a scrollport that is not laid out yet is
             * still watched and the first real layout publishes on its own.
             */
            if (scrollport !== observed) {
                observer?.disconnect();
                observed = scrollport;
                observer = scrollport ? new ResizeObserver(publish) : undefined;
                observer?.observe(scrollport as Element);
            }

            /*
             * A zero-height scrollport is not laid out — a tab opened in the background, or
             * jsdom, which has no layout engine. Its rect reads all zeroes, which would
             * claim the scrollport starts at the top of the viewport and has no height.
             * Leave the last good values (or the CSS fallbacks) alone; the observer above
             * fires once it has a size.
             */
            if (scrollport?.clientHeight === 0) {
                return;
            }

            if (scrollport) {
                /*
                 * `clientHeight` is the padding box — the area content scrolls through and
                 * sticky pins within — so the top edge steps past the border to match.
                 */
                const top = scrollport.getBoundingClientRect().top + scrollport.clientTop;
                write(SCROLLPORT_TOP, `${Math.round(top)}px`);
                write(SCROLLPORT_HEIGHT, `${scrollport.clientHeight}px`);
            } else {
                write(SCROLLPORT_TOP, '0px');
                write(SCROLLPORT_HEIGHT, '');
            }
        };

        publish();
        globalThis.addEventListener('resize', publish);

        return () => {
            globalThis.removeEventListener('resize', publish);
            observer?.disconnect();
            target.style.removeProperty(SCROLLPORT_TOP);
            target.style.removeProperty(SCROLLPORT_HEIGHT);
        };
    }, [targetRef]);
};
